/**
 * Tab-side OddsPapi scope registry — the odds half of the watched-game lane.
 *
 * A trader on ONE game needs that game's odds as fast as they can be had; a
 * board needs every game's. Each OP-rendering view declares which it is, the
 * registry unions across the tab, and the fleet (useHydra's sink) hands the
 * result to every OP-capable client, whose shared worker then clones only
 * those games' frames into this tab (`HydraClient.setOpScope`).
 *
 * Union rule — fail WIDE, never dark:
 *   - any source declares 'all'  → the whole channel;
 *   - NO source has declared     → the whole channel (a view that predates
 *     this registry keeps working exactly as before);
 *   - otherwise                  → the union of the declared game keys.
 *
 * Widening publishes immediately (a newly opened game must not wait for its
 * odds). Narrowing waits out a short grace, so board → game → board does not
 * trim the store and then pay a whole-slate burst to refill it.
 *
 * Outside Zustand on purpose, like feedNeeds: a scope change must never
 * re-render anything — it only changes what reaches this tab.
 */
export type OpScope = 'all' | readonly string[];
export type OpScopeSink = (games: string[] | null) => void;

export const OP_NARROW_GRACE_MS = 3_000;

const _sources = new Map<string, OpScope>();
// Several transports can carry OP for one tab: the fleet's ladder (in-shell
// pages) and the standalone lane's own client (the sniper screen).
const _sinks = new Set<OpScopeSink>();
/** Last published scope: null = whole channel. */
let _published: string[] | null = null;
let _narrowTimer: ReturnType<typeof setTimeout> | null = null;

function compute(): string[] | null {
  if (_sources.size === 0) return null;
  const keys = new Set<string>();
  for (const scope of _sources.values()) {
    if (scope === 'all') return null;
    for (const k of scope) keys.add(k);
  }
  return [...keys].sort();
}

/** `next` asks for nothing `prev` was not already delivering. */
function isNarrowing(prev: string[] | null, next: string[] | null): boolean {
  if (next == null) return false;
  if (prev == null) return true;
  const had = new Set(prev);
  return next.every((k) => had.has(k));
}

function same(a: string[] | null, b: string[] | null): boolean {
  if (a == null || b == null) return a === b;
  return a.length === b.length && a.every((k, i) => k === b[i]);
}

function publish(next: string[] | null): void {
  _published = next;
  for (const sink of _sinks) sink(next);
}

// The feed gateway takes the scope UNGRACED: it is where a game page's keys
// come from, a board → game click must reach it at once, and the narrowing
// grace that protects board → game → board lives server-side there.
const _rawSinks = new Set<OpScopeSink>();
let _raw: string[] | null = null;

/** Attach a sink that sees every scope change immediately (no narrow grace). */
export function addOpScopeRawSink(sink: OpScopeSink): () => void {
  _rawSinks.add(sink);
  sink(_raw);
  return () => {
    _rawSinks.delete(sink);
  };
}

function reconcile(): void {
  const next = compute();
  if (!same(next, _raw)) {
    _raw = next;
    for (const sink of _rawSinks) sink(next);
  }
  if (_narrowTimer != null) {
    clearTimeout(_narrowTimer);
    _narrowTimer = null;
  }
  if (same(next, _published)) return;
  if (!isNarrowing(_published, next)) {
    publish(next);
    return;
  }
  _narrowTimer = setTimeout(() => {
    _narrowTimer = null;
    const settled = compute();
    if (!same(settled, _published)) publish(settled);
  }, OP_NARROW_GRACE_MS);
}

/** Declare (or clear, with `null`) one source's OP scope. A source changing
 *  its declaration re-declares over the top — never clear first (the clear
 *  would publish a union with the source missing). */
export function declareOpNeeds(sourceId: string, scope: OpScope | null): void {
  if (scope == null) _sources.delete(sourceId);
  else _sources.set(sourceId, scope);
  reconcile();
}

/** Attach a transport. The current scope is published to it immediately, so a
 *  declaration made before the sockets existed is not lost. Returns detach. */
export function addOpScopeSink(sink: OpScopeSink): () => void {
  _sinks.add(sink);
  sink(_published);
  return () => {
    _sinks.delete(sink);
  };
}

/** Tests only. */
export function resetOpNeeds(): void {
  _sources.clear();
  _sinks.clear();
  _rawSinks.clear();
  _raw = null;
  _published = null;
  if (_narrowTimer != null) clearTimeout(_narrowTimer);
  _narrowTimer = null;
}
